/**
 * ----------------------------------------------------------------------------------------------------------------------
 * PRO TABS SLIDER
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Collection tabs above a horizontal product slider.
 *
 * Every panel is rendered server-side by the section, so switching tabs is an attribute change — no fetch, no
 * loading state, nothing to fail, and the product cards are the theme's own in every panel.
 *
 * Scrolling itself is native CSS (`overflow-x` + `scroll-snap`), which is what gives correct touch and trackpad
 * behaviour for free. This element only adds the tab switching, the arrow buttons, the dots, and keyboard
 * support — it never drives the scroll position frame by frame.
 */

(() => {
  if (window.customElements.get('pro-tabs-slider')) return;

  class ProTabsSlider extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-pts-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-pts-panel]'));
      if (!this.panels.length) return;

      this.listeners = new AbortController();
      const signal = this.listeners.signal;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event, index), { signal });
      });

      this.panels.forEach((panel) => this.initPanel(panel, signal));

      /* Card width is a percentage of the track, so a resize changes how many pages exist. */
      window.addEventListener('resize', this.refreshAll.bind(this), { signal });

      this.select(Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')));
    }

    disconnectedCallback() {
      this.listeners?.abort();
    }

    /* ----------------------------------------------------------------------------------------------------
     * Tabs
     * -------------------------------------------------------------------------------------------------- */

    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        /* Roving tabindex: only the active tab is a tab stop, so Tab moves past the strip, not through it. */
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        panel.toggleAttribute('hidden', i !== index);
      });

      /**
       * A hidden element measures as zero width, so a newly revealed panel's arrows and dots can only be
       * calculated once it is visible — hence refreshing after the toggle rather than before.
       */
      const panel = this.panels[index];
      if (panel) this.refresh(panel);
    }

    onTabKeydown(event, index) {
      const last = this.tabs.length - 1;
      let next = null;

      if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;

      event.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }

    /* ----------------------------------------------------------------------------------------------------
     * Slider
     * -------------------------------------------------------------------------------------------------- */

    initPanel(panel, signal) {
      const track = panel.querySelector('[data-pts-track]');
      if (!track) return;

      const prev = panel.querySelector('[data-pts-prev]');
      const next = panel.querySelector('[data-pts-next]');

      prev?.addEventListener('click', () => this.page(track, -1), { signal });
      next?.addEventListener('click', () => this.page(track, 1), { signal });

      track.addEventListener('scroll', () => this.refresh(panel), { signal, passive: true });

      panel.querySelectorAll('[data-pts-dot]').forEach((dot, i) => {
        dot.addEventListener('click', () => this.goToPage(track, i), { signal });
      });
    }

    /** One "page" is a full track width, so paging never leaves a card half-visible at the leading edge. */
    page(track, direction) {
      track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
    }

    goToPage(track, pageIndex) {
      track.scrollTo({ left: pageIndex * track.clientWidth, behavior: 'smooth' });
    }

    refreshAll() {
      this.panels.forEach((panel) => {
        if (!panel.hasAttribute('hidden')) this.refresh(panel);
      });
    }

    refresh(panel) {
      const track = panel.querySelector('[data-pts-track]');
      if (!track) return;

      /* Sub-pixel track widths mean scrollLeft rarely lands exactly on either end. */
      const TOLERANCE = 2;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const scrollable = maxScroll > TOLERANCE;

      const prev = panel.querySelector('[data-pts-prev]');
      const next = panel.querySelector('[data-pts-next]');

      /**
       * Arrows are hidden outright when everything fits, and merely disabled at each end otherwise — so the
       * control never disappears mid-interaction and shifts the layout under the pointer.
       */
      [prev, next].forEach((btn) => {
        if (btn) btn.toggleAttribute('hidden', !scrollable);
      });
      if (prev) prev.disabled = track.scrollLeft <= TOLERANCE;
      if (next) next.disabled = track.scrollLeft >= maxScroll - TOLERANCE;

      const dotsWrap = panel.querySelector('[data-pts-dots]');
      if (!dotsWrap) return;

      dotsWrap.toggleAttribute('hidden', !scrollable);

      const dots = Array.from(dotsWrap.querySelectorAll('[data-pts-dot]'));
      if (!dots.length) return;

      const pages = Math.max(1, Math.ceil(track.scrollWidth / Math.max(1, track.clientWidth)));
      const current = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));

      dots.forEach((dot, i) => {
        /* The dot count is rendered for the widest case, so surplus dots are hidden per breakpoint. */
        dot.toggleAttribute('hidden', i >= pages);
        dot.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
    }
  }

  window.customElements.define('pro-tabs-slider', ProTabsSlider);
})();
